import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Ellipsis, Paperclip, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { isEmpty } from "lodash-es";

import { AppStateContext } from "@/state/AppStateContext";
import { deleteConversation, getConversation, pinConversation, renameConversation } from "@/api/api";
import { Conversation } from "@/api/models";
import { clearAgent, clearChatTransientState, clearCustomGPT, doTrackEvent, newChat } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ConversationShareDialog from "@/components/Chat/ConversationShareDialog";

interface ChatHistoryListItemCellProps {
  item?: Conversation;
}

const ChatHistoryListItemCell = ({ item }: ChatHistoryListItemCellProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleNewChat = () => newChat(appStateContext);
  const [customGPTName, setCustomGPTName] = useState<string>("");
  const [agentName, setAgentName] = useState<string>("");
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>("");
  const [textFieldFocused, setTextFieldFocused] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [focusDelete, setFocusDelete] = useState<boolean>(false);

  let isSelected = item?.id === appStateContext?.state.currentChat?.id;

  useEffect(() => {
    const foundCustomGPT = appStateContext?.state.customGPTs?.find(c => c.id == item?.customGPTID);
    setCustomGPTName(foundCustomGPT?.name || '');
    const foundAgent = appStateContext?.state.agents?.find(a => a.id == item?.selected_tool);
    setAgentName(foundAgent?.name || '');
  }, [item, appStateContext?.state.customGPTs, appStateContext?.state.agents]);

  /**
   * Toggle editing off
  **/
  useEffect(() => {
    if (appStateContext?.state.currentChat?.id !== item?.id) {
      setEdit(false);
      setEditTitle('');
    }
  }, [appStateContext?.state.currentChat?.id, item?.id]);

  /**
   * Toggle textfield focus
  **/
  useEffect(() => {
    if (textFieldFocused) setTextFieldFocused(false);
  }, [textFieldFocused]);

  /**
   * Handle selecting and opening a conversation
  **/
  const handleSelectItem = () => {
    if (item?.id != appStateContext?.state.currentChat?.id) {
      clearChatTransientState(appStateContext);
      clearCustomGPT(appStateContext);
      clearAgent(appStateContext);
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
      navigate(`/c/${item?.id}`);
      handleTrackEvent('click', 'conv_open', 'Conversation - Open', item?.id || '', '', '');
    }
  }

  /**
   * Handle pinning a conversation
  **/
  const onPin = () => {
    setIsPopoverOpen(false);
    appStateContext?.dispatch({ type: 'UPDATE_CONVERSATION_PIN', payload: { ...item, pinned: 1 } as Conversation });
    pinConversation(item?.id || "", 1);
    handleTrackEvent('click', 'conv_pin', 'Conversation - Pin', item?.id || '', '', '');
  }

  /**
   * Handle unpinning a conversation
  **/
  const onUnPin = () => {
    setIsPopoverOpen(false);
    appStateContext?.dispatch({ type: 'UPDATE_CONVERSATION_PIN', payload: { ...item, pinned: 0 } as Conversation });
    pinConversation(item?.id || "", 0);
    handleTrackEvent('click', 'conv_unpin', 'Conversation - Unpin', item?.id || '', '', '');
  }

  /**
   * Handle renaming a conversation
  **/
  const onRename = () => {
    setEdit(true);
    setTextFieldFocused(true);
    setEditTitle(item?.title || "");
    handleTrackEvent('click', 'conv_rename', 'Conversation - Rename', item?.id || '', '', '');
  }

  /**
   * Handle typing while editing
  **/
  const onEditKeyUp = (e: any) => {
    // If escape, cancel editing
    if (e.key === "Escape") {
      setEditTitle("");
      setTextFieldFocused(false);
      setEdit(false);
    }
    // If enter, change title
    else if (e.key === "Enter") {
      renameConversation(item?.id || "", editTitle);
      appStateContext?.dispatch({ type: 'UPDATE_CONVERSATION_TITLE', payload: { ...item, title: editTitle } as Conversation });
      setEditTitle("");
      setTextFieldFocused(false);
      setEdit(false);
    }
    // Otherwise, update edited title
    else setEditTitle(e.target.value);
  }

  /**
   * Handle downloading a conversation
  **/
  const onDownload = async () => {
    setIsDownloading(true);

    // Get conversation messages
    const conversation = await getConversation(item?.id || '', false);

    // Build string with all messages except tool messages, separated by new lines
    const chat = conversation?.messages.reduce((result, msg) => {
      return (msg.role !== 'tool' ? `${result}${msg.role}: ${msg.content} \r\n \r\n \r\n` : `${result}`);
    }, "");
    const file = new Blob([chat || ""], { type: 'text/plain' });
    const fileUrl = URL.createObjectURL(file);
    const downloadTitle = conversation?.title?.trim() || "conversation";

    // Create a temporary link element to trigger the download
    const element = document.createElement("a");
    element.href = fileUrl;
    element.download = downloadTitle.toLowerCase().endsWith(".txt") ? downloadTitle : `${downloadTitle}.txt`;
    document.body.appendChild(element);
    element.click();
    element.remove();
    URL.revokeObjectURL(fileUrl);
    handleTrackEvent('click', 'conv_download', 'Conversation - Download', item?.id || '', '', '');
    setIsDownloading(false);
  }

  /**
   * Handle deleting a conversation
   * 
   * @param {React.MouseEvent} e - The mouse click event
  **/
  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPopoverOpen(false);
    isSelected = false;
    handleNewChat();
    appStateContext?.dispatch({ type: 'DELETE_CONVERSATION', payload: item?.id || "" });
    deleteConversation(item?.id || "");
    navigate('/');
    handleTrackEvent('click', 'conv_delete', 'Conversation - Delete', item?.id || '', '', '');
  }
  
  if (!item) return null;

  return (
    <div className={`group flex justify-between items-center gap-2 w-full hover:bg-neutral ${isSelected ? `bg-neutral` : ``} rounded-lg cursor-pointer p-2 mt-1`} onClick={handleSelectItem}>
      {edit ?
        <input type="text" className="bg-neutral border border-secondary rounded text-[13px] px-1" autoFocus={textFieldFocused} defaultValue={editTitle} onKeyUp={onEditKeyUp} />
      :
        <div className="flex flex-col truncate">
          <div className="flex items-center gap-1 truncate text-[13px] dark:text-white" title={item?.title || ''}>
            {item?.title}
          </div>
          <div className="truncate text-[11px] ms-2">{customGPTName || agentName || ''}</div>
        </div>
      }
      
      <div className="h-[20px]">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger onClick={(e) => e.stopPropagation()}>
            {/* Hover swaps are gated to hover-capable devices: on touch, a tap that changes visibility is consumed as a hover and the row needs a second tap to open */}
            {(item?.pinned == 1 && !isPopoverOpen) ? <div className="hidden [@media(hover:hover)]:block [@media(hover:hover)]:group-hover:hidden rounded-xl h-[20px] ps-2 mt-1"><Pin size={14} /></div>
            : (!isEmpty(item?.files) && !isPopoverOpen) ? <div className="hidden [@media(hover:hover)]:block [@media(hover:hover)]:group-hover:hidden rounded-xl h-[20px] ps-2 mt-1"><Paperclip size={14} /></div>
            : null}
            <div className="[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 hover:bg-base-100 rounded-xl cursor-pointer px-1" onClick={() => handleTrackEvent('click', 'conv_menu', 'Conversation - Menu', item?.id || '', '', '')}><Ellipsis size={20} /></div>
          </PopoverTrigger>
          <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[150px] dark:text-white p-3">
            {item?.pinned == 1 ?
              <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3" onClick={onUnPin}>
                <PinOff size={16} />
                <div className="select-none text-sm">Unpin</div>
              </div>
            : 
              <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3" onClick={onPin}>
                <Pin size={16} />
                <div className="select-none text-sm">Pin</div>
              </div>
            }
            <ConversationShareDialog
              conversation={item}
              trackEventItem="conv_share"
              trackEventText="Conversation - Share"
              trigger={
                <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3 mt-1">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="" className="-ms-0.5 icon"><path d="M2.66821 12.6663V12.5003C2.66821 12.1331 2.96598 11.8353 3.33325 11.8353C3.70052 11.8353 3.99829 12.1331 3.99829 12.5003V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663ZM9.33521 12.5003V4.9388L7.13696 7.13704C6.87732 7.39668 6.45625 7.39657 6.19653 7.13704C5.93684 6.87734 5.93684 6.45631 6.19653 6.19661L9.52954 2.86263L9.6311 2.77962C9.73949 2.70742 9.86809 2.66829 10.0002 2.66829C10.1763 2.66838 10.3454 2.73819 10.47 2.86263L13.804 6.19661C14.0633 6.45628 14.0634 6.87744 13.804 7.13704C13.5443 7.39674 13.1222 7.39674 12.8625 7.13704L10.6653 4.93977V12.5003C10.6651 12.8673 10.3673 13.1652 10.0002 13.1654C9.63308 13.1654 9.33538 12.8674 9.33521 12.5003Z"></path></svg>
                  <div className="select-none text-sm">Share</div>
                </div>
              }
            />
            <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3 mt-1" onClick={onRename}>
              <Pencil size={16} />
              <div className="select-none text-sm">Rename</div>
            </div>
            <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3 mt-1" onClick={onDownload}>
              <Download size={16} />
              <div className="select-none text-sm">Download</div>
              <Dialog open={isDownloading} onOpenChange={setIsDownloading}>
                <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
                  <DialogHeader>
                    <DialogDescription>
                      <div className="flex items-center gap-2">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                        Downloading Conversation...
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <Dialog>
              <DialogTrigger className="w-full" onClick={(e) => { e.stopPropagation(); setFocusDelete(true); }}>
                <div className="flex items-center gap-3 hover:bg-error hover:bg-opacity-25 rounded-xl cursor-pointer py-2 px-3 mt-1">
                  <Trash2 className="text-error" size={16} />
                  <div className="select-none text-sm text-error">Delete</div>
                </div>
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
                <DialogHeader>
                  <DialogTitle className="mb-3">Delete chat?</DialogTitle>
                  <DialogDescription>Are you sure that you want to delete <strong>{item?.title}</strong>?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
                  <DialogClose asChild><button type="button" className="btn btn-error" autoFocus={focusDelete} onClick={(e) => onDelete(e)}>Delete</button></DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export { ChatHistoryListItemCell };
